import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import theme from './Budget.module.scss';
import { IState } from '@/state/IState';
import { Trans } from '@lingui/react/macro';
import BudgetListItem from './BudgetList/BudgetListItem';
import { loadBuyerBudget } from '@/state/buyer/budget/creators';
import { useAppDispatch, useBudgetGroupedByRoom, useIsMobile } from '@/utils/hooks';
import { useBuyerRoomMap } from '@/services/rooms';
import { BuyerCurrencyDisplay } from '../BuyerCurrencyDisplay';
import BuyerPageTitle from '../BuyerPageTitle/BuyerPageTitle';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import Banner from '@/components/Banner';
import { BudgetItemPriceType } from '@/types';

type BudgetProps = {};

const Budget: React.FC<BudgetProps> = () => {
    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();
    const budget = useSelector((state: IState) => state.buyer.budget);
    const groupedByRoom = useBudgetGroupedByRoom();
    const residentialUnitId = useSelector((state: IState) => state.buyer.permissions?.residentialUnitId ?? null);
    // Room names are only rendered when grouping by room; skip the fetch otherwise.
    const roomMap = useBuyerRoomMap(groupedByRoom ? residentialUnitId : null);

    useEffect(() => {
        dispatch(loadBuyerBudget());
    }, [dispatch]);

    if (!budget) {
        return <LoadingIndicator />;
    }

    const renderTotals = () => (
        <>
            <div className={theme.total}>
                {budget.unsignedPrice > 0 && (
                    <div>
                        <span>
                            <Trans>Unsigned</Trans>
                            <small>
                                <Trans>Must be signed before the deadline expires</Trans>
                            </small>
                        </span>
                        <span>
                            <BuyerCurrencyDisplay value={budget.unsignedPrice} />
                        </span>
                    </div>
                )}

                {budget.signedPrice > 0 && (
                    <div>
                        <span>
                            <Trans>Signed</Trans>
                        </span>
                        <span>
                            <BuyerCurrencyDisplay value={budget.signedPrice} />
                        </span>
                    </div>
                )}

                {budget.extraCostPrice > 0 && (
                    <div>
                        <span>
                            <Trans>Extra costs</Trans>
                        </span>
                        <span>
                            <BuyerCurrencyDisplay value={budget.extraCostPrice} />
                        </span>
                    </div>
                )}

                <div className={theme.grandTotal}>
                    <span>
                        <Trans>Total</Trans>
                    </span>
                    <span>
                        <BuyerCurrencyDisplay value={budget.totalPrice} />
                    </span>
                </div>
            </div>
        </>
    );

    const hasUnsignedOptions = budget.groups
        .flatMap((x) => x.items)
        .some((x) => x.priceType === BudgetItemPriceType.Unsigned);
    return (
        <div className={theme.container}>
            <BuyerPageTitle>
                <Trans>Budget</Trans>
            </BuyerPageTitle>
            {/* <BuyerForcedStyleSelection onAccept={() => dispatch(loadBuyerBudget())} /> */}
            {hasUnsignedOptions && (
                <Banner type="warning">
                    <Trans>Some of your options are not signed</Trans>
                </Banner>
            )}

            {isMobile && renderTotals()}

            {budget.groups.map((b, index) => (
                <BudgetListItem key={index} data={b} roomMap={roomMap} groupedByRoom={groupedByRoom} />
            ))}

            {!isMobile && renderTotals()}
        </div>
    );
};

export default Budget;
